import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import BackChevronIconSvg from '../../../assets/icons/icon-back-chevron.svg?react';
import IconHelpOutlineSvg from '../../../assets/icons/icon-help-outline.svg?react';
import { parseAmount } from '../../../shared/model';
import {
  BaseButton,
  BottomNavPageLayout,
  CurrencyAmountInput,
  HomeDashboardBottomNav,
  PrimaryButton,
  rubikTypographyStyles,
  UserAvatar,
} from '../../../shared/ui';

import {
  WithdrawalTransferSheet,
  type WithdrawalTransferSheetStatus,
} from './withdrawal-transfer-sheet';

type WithdrawalAmountScreenProps = {
  avatarUrl?: string | null;
  availableBalance: string;
  availableBalanceAmount: number;
  currencyPrefix: string;
  destinationTaxId: string;
  onBack: () => void;
  onOpenSupport?: () => void;
  onOpenHome: () => void;
  onOpenMyPage: () => void;
  onSubmitTransfer?: (amount: number) => Promise<unknown> | unknown;
  taxIdLabelKey: string;
  thousandsSeparator: string;
};

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 29;
const HEADER_HEIGHT_PX = 56;
const FOOTER_TOP_PADDING_PX = 10;
const FOOTER_BOTTOM_PADDING_PX = 13;

const Screen = styled.div.attrs({
  'data-ui': 'withdrawal-amount-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
`;

const Header = styled.header.attrs({
  'data-ui': 'withdrawal-amount-header',
})`
  min-height: ${({ theme }) => theme.scale.px(HEADER_HEIGHT_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
  display: grid;
  grid-template-columns:
    ${({ theme }) => theme.scale.px(24)} minmax(0, 1fr)
    ${({ theme }) => theme.scale.px(39)};
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(20)};
`;

const BackButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(24)};
  height: ${({ theme }) => theme.scale.px(24)};
  display: grid;
  place-items: center;
`;

const BackIcon = styled(BackChevronIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(12)};
  height: ${({ theme }) => theme.scale.px(20)};
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

const Title = styled.h1`
  ${rubikTypographyStyles};
  margin: 0;
  min-width: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SupportButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(39)};
  height: ${({ theme }) => theme.scale.px(39)};
  display: grid;
  place-items: center;
`;

const SupportIcon = styled(IconHelpOutlineSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  --help-icon-bg: ${({ theme }) => theme.colors.support.infoSubtle};
  --help-icon-fg: ${({ theme }) => theme.colors.text.primary};
`;

const Body = styled.div.attrs({
  'data-ui': 'withdrawal-amount-body',
})`
  flex: 1;
  min-height: 0;
  display: grid;
  align-content: start;
`;

const BalanceBlock = styled.section.attrs({
  'data-ui': 'withdrawal-amount-balance-block',
})`
  justify-self: center;
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.scale.px(4)};
  padding-top: ${({ theme }) => theme.scale.px(31)};
`;

const BalanceLabel = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.5;
`;

const BalanceValue = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AmountBand = styled.section.attrs({
  'data-ui': 'withdrawal-amount-band',
})`
  margin-top: ${({ theme }) => theme.scale.px(28)};
  min-height: ${({ theme }) => theme.scale.px(66)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const AmountInput = styled(CurrencyAmountInput)`
  width: 100%;
  max-width: 100%;
  background: transparent;
  border: 0;
  font-weight: 600;
`;

const BalanceNotice = styled.p.attrs({
  'data-ui': 'withdrawal-amount-balance-notice',
})`
  min-height: ${({ theme }) => theme.scale.px(15)};
  margin: ${({ theme }) => theme.scale.px(6)} 0 0;
  justify-self: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.support.danger};
  opacity: 0.7;
`;

const RecipientCard = styled.section.attrs({
  'data-ui': 'withdrawal-amount-recipient-card',
})`
  margin: ${({ theme }) => theme.scale.px(24)}
    ${({ theme }) =>
      `max(${theme.scale.px(
        FRAME_SIDE_PADDING_PX,
      )}, env(safe-area-inset-right))`}
    0
    ${({ theme }) =>
      `max(${theme.scale.px(
        FRAME_SIDE_PADDING_PX,
      )}, env(safe-area-inset-left))`};
  min-height: ${({ theme }) => theme.scale.px(58)};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(9)}
    ${({ theme }) => theme.scale.px(14)};
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(10)};
`;

const RecipientText = styled.div`
  min-width: 0;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(2)};
`;

const RecipientLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RecipientTaxId = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.5;
`;

const ActionSlot = styled.div`
  padding-top: ${({ theme }) => theme.scale.px(12)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const ContinueButton = styled(PrimaryButton).attrs({
  'data-ui': 'withdrawal-amount-continue-button',
  type: 'button',
})`
  width: 100%;

  &:disabled {
    opacity: 0.4;
  }
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'withdrawal-amount-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

export function WithdrawalAmountScreen({
  avatarUrl = null,
  availableBalance,
  availableBalanceAmount,
  currencyPrefix,
  destinationTaxId,
  onBack,
  onOpenSupport,
  onOpenHome,
  onOpenMyPage,
  onSubmitTransfer,
  taxIdLabelKey,
  thousandsSeparator,
}: WithdrawalAmountScreenProps) {
  const { t } = useTranslation();
  const [amountValue, setAmountValue] = useState('');
  const [transferSheetStatus, setTransferSheetStatus] =
    useState<WithdrawalTransferSheetStatus | null>(null);
  const taxIdLabel = t(taxIdLabelKey);
  const enteredAmount = parseAmount(amountValue);
  const hasAmount = enteredAmount !== null && enteredAmount > 0;
  const hasInsufficientBalance =
    enteredAmount !== null && enteredAmount > availableBalanceAmount;
  const canContinue = hasAmount && !hasInsufficientBalance;

  async function handleContinue() {
    if (enteredAmount === null || !canContinue) {
      return;
    }

    setTransferSheetStatus('request-sent');

    let submitPromise: Promise<unknown>;
    try {
      submitPromise = Promise.resolve(onSubmitTransfer?.(enteredAmount));
    } catch {
      setTransferSheetStatus('failure');
      return;
    }

    await Promise.resolve();
    setTransferSheetStatus('processing');

    try {
      await submitPromise;
      setTransferSheetStatus('success');
    } catch {
      setTransferSheetStatus('failure');
    }
  }

  return (
    <BottomNavPageLayout
      frameDataUi="withdrawal-amount-page-frame"
      pageDataUi="withdrawal-amount-page"
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab={null}
            onOpenHome={onOpenHome}
            onOpenMyPage={onOpenMyPage}
          />
        </BottomNavSlot>
      }
    >
      <Screen>
        <Header>
          <BackButton
            aria-label={t('withdrawal.backAria')}
            type="button"
            onClick={onBack}
          >
            <BackIcon />
          </BackButton>
          <Title>{t('withdrawal.title')}</Title>
          <SupportButton
            aria-label={t('myPage.helpAria')}
            type="button"
            onClick={() => {
              onOpenSupport?.();
            }}
          >
            <SupportIcon />
          </SupportButton>
        </Header>

        <Body>
          <BalanceBlock>
            <BalanceLabel>{t('withdrawal.availableBalanceLabel')}</BalanceLabel>
            <BalanceValue>{availableBalance}</BalanceValue>
          </BalanceBlock>

          <AmountBand>
            <AmountInput
              ariaLabel={t('withdrawal.amountAria')}
              currencyPrefix={currencyPrefix}
              dataUi="withdrawal-amount-input"
              onValueChange={setAmountValue}
              textAlign="center"
              thousandsSeparator={thousandsSeparator}
              value={amountValue}
              variant="amount"
            />
          </AmountBand>

          <BalanceNotice>
            {hasInsufficientBalance ? t('withdrawal.insufficientBalance') : ''}
          </BalanceNotice>

          <RecipientCard>
            <UserAvatar avatarUrl={avatarUrl} sizePx={40} />
            <RecipientText>
              <RecipientLabel>
                {t('withdrawal.beneficiaryLabel', { taxIdLabel })}
              </RecipientLabel>
              <RecipientTaxId>{destinationTaxId}</RecipientTaxId>
            </RecipientText>
          </RecipientCard>

          <ActionSlot>
            <ContinueButton disabled={!canContinue} onClick={handleContinue}>
              {t('withdrawal.transfer')}
            </ContinueButton>
          </ActionSlot>
        </Body>

        <WithdrawalTransferSheet
          onOpenSupport={onOpenSupport}
          onClose={() => {
            setTransferSheetStatus(null);
          }}
          status={transferSheetStatus}
        />
      </Screen>
    </BottomNavPageLayout>
  );
}
